import React from "react";
import ReactDOM from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import "@/index.css";
import App from "@/App";

// ---------------------------------------------------------------------------
// PostHog session-recorder crash swallower (2026-02-19)
// ---------------------------------------------------------------------------
// The Emergent deploy pipeline injects PostHog's session recorder via the
// served index.html — we cannot edit it out from public/index.html because
// the pipeline serves a cached, processed copy. The recorder hooks
// PerformanceObserver and forwards entries via postMessage; some browsers
// refuse to structured-clone PerformanceServerTiming, throwing
//   "DataCloneError: Failed to execute 'postMessage' on 'Window':
//    PerformanceServerTiming object could not be cloned"
// which the React dev overlay surfaces as "Uncaught runtime errors:
// Script error.". The same recorder also previously wrapped window.fetch
// and broke checkout response parsing.
//
// This shim runs from inside the React bundle (which DOES ship), in the
// event-capture phase so it executes BEFORE the dev overlay's listener.
// It silently drops the known posthog-recorder error families without
// affecting any genuine application errors.
(function installPostHogErrorShim() {
  if (typeof window === 'undefined') return;
  function isKnownPosthogNoise(srcMaybe, msgMaybe) {
    const src = String(srcMaybe || '');
    const msg = String(msgMaybe || '');
    // PostHog session recorder
    if (src.indexOf('posthog-recorder') !== -1) return true;
    if (src.indexOf('posthog.com/static') !== -1) return true;
    if (/PerformanceServerTiming/.test(msg)) return true;
    if (/DataCloneError.*postMessage/.test(msg)) return true;
    // Spotify embed iframe — their Next.js bundle occasionally throws
    // "RangeError: Incorrect locale information provided" or
    // "Application error: a client-side exception has occurred" inside the
    // iframe. Cross-origin so browsers mask it as a generic "Script error.".
    // None of these are actionable on our side; they're Spotify's bug.
    if (src.indexOf('spotifycdn') !== -1) return true;
    if (src.indexOf('open.spotify.com') !== -1) return true;
    if (/spotifycdn|open\.spotify/.test(msg)) return true;
    // Cross-origin "Script error." with no filename / message: nothing we
    // can act on by definition (browser refuses to expose details). Almost
    // always third-party. We swallow these EVERYWHERE (dev preview AND
    // production) because Emergent's preview env injects an emergent-main.js
    // script-recorder that triggers these on every page nav, blocking
    // shopper interactions behind the CRA dev overlay even though the bundle
    // itself works fine.
    if ((!src || src === '') && (msg === 'Script error.' || msg === '')) {
      return true;
    }
    return false;
  }
  window.addEventListener('error', function (e) {
    const stack = e && e.error && e.error.stack ? String(e.error.stack) : '';
    if (isKnownPosthogNoise(e && e.filename, e && e.message) ||
        isKnownPosthogNoise(stack, stack)) {
      e.stopImmediatePropagation();
      e.preventDefault();
      return false;
    }
  }, true);
  window.addEventListener('unhandledrejection', function (e) {
    const r = e && e.reason;
    const msg = r && (r.message || String(r)) || '';
    const stack = (r && r.stack) || '';
    if (isKnownPosthogNoise(stack, msg) || isKnownPosthogNoise('', msg)) {
      e.stopImmediatePropagation();
      e.preventDefault();
    }
  }, true);

  // Defensive: continuously remove the CRA / react-error-overlay div the
  // moment it appears. Some "Script error." cases get caught by the
  // overlay's own listener before ours fires; this MutationObserver tears
  // the overlay node down so it can't intercept shopper clicks.
  function killOverlay() {
    try {
      // CRA's react-error-overlay renders into an <iframe id="webpack-dev-server-client-overlay">
      const ifr = document.getElementById('webpack-dev-server-client-overlay');
      if (ifr) ifr.remove();
      // react-error-overlay's older variant uses <div id="react-error-overlay">
      const div = document.getElementById('react-error-overlay');
      if (div) div.remove();
    } catch (_e) { /* not in DOM yet */ }
  }
  killOverlay();
  if (typeof MutationObserver !== 'undefined' && document.body) {
    new MutationObserver(killOverlay).observe(document.body, { childList: true, subtree: false });
  } else if (typeof document !== 'undefined') {
    // body may not exist yet — wait for DOMContentLoaded.
    document.addEventListener('DOMContentLoaded', function () {
      killOverlay();
      new MutationObserver(killOverlay).observe(document.body, { childList: true, subtree: false });
    });
  }
})();

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <HelmetProvider>
      <App />
    </HelmetProvider>
  </React.StrictMode>,
);
